import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, HOME_FOR } from "@/context/AuthContext";
import api from "@/lib/api";
import { GPA_LOGO } from "@/lib/assets";
import { UserMenu } from "@/components/UserMenu";
import { ReferButton } from "@/components/ReferButton";
import { downloadElementPdf } from "@/lib/pdf";
import { Button } from "@/components/ui/button";
import { LogOut, Menu, X, FileDown, Loader2, Eye, Info } from "lucide-react";

const EA_DISMISS_KEY = "gpaplus_ea_dismissed";

const ROLE_LABEL = {
  student: "Student", advisor: "Academic Advisor", faculty: "Faculty", admin: "Website Administrator", employer: "Employer",
};

export function DashboardShell({ title, subtitle, nav, children }) {
  const { user, logout, exitPreview } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [navBadges, setNavBadges] = useState({});
  const [showEaNotice, setShowEaNotice] = useState(() => {
    try { return sessionStorage.getItem(EA_DISMISS_KEY) !== "1"; } catch { return true; }
  });
  const contentRef = useRef(null);

  const dismissEaNotice = () => {
    try { sessionStorage.setItem(EA_DISMISS_KEY, "1"); } catch { /* ignore */ }
    setShowEaNotice(false);
  };

  useEffect(() => {
    if (user?.role === "student") {
      api.get("/student/reports/unread_count")
        .then(({ data }) => setNavBadges({ "/app/student/advisor-updates": data.count }))
        .catch(() => {});
    }
  }, [user?.role, pathname]);

  const doLogout = async () => { await logout(); navigate("/login"); };

  const doExitPreview = async () => {
    if (exiting) return;
    setExiting(true);
    try {
      const u = await exitPreview();
      navigate(HOME_FOR[u.role]);
    } catch { toast.error("Could not exit preview. Please try again."); }
    finally { setExiting(false); }
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      await downloadElementPdf(contentRef.current, `GPA+ ${title || "Dashboard"}.pdf`);
      toast.success("PDF downloaded.");
    } catch {
      toast.error("Couldn't generate the PDF. Please try again.");
    } finally {
      setPdfBusy(false);
    }
  };

  const Sidebar = (
    <aside className="flex w-full flex-col bg-[#003057] p-5 text-white md:h-screen md:w-64 md:flex-shrink-0">
      <Link to="/" className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5">
        <img src={GPA_LOGO} alt="GPA+" className="h-8 w-auto" />
      </Link>
      <div className="mt-8 space-y-1">
        {nav.map((n) => {
          const Icon = n.icon;
          const active = pathname === n.to;
          return (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
              data-testid={`sidebar-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors ${
                active ? "bg-[#78be21] text-[#00243f]" : "text-slate-300 hover:bg-white/10 hover:text-white"
              }`}>
              <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} /> {n.label}
              {navBadges[n.to] > 0 && (
                <span data-testid="nav-badge" className="ml-auto rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">{navBadges[n.to]}</span>
              )}
            </Link>
          );
        })}
      </div>
      <div className="mt-auto hidden pt-6 md:block">
        <div className="rounded-xl bg-white/8 p-3.5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[#78be21]">{ROLE_LABEL[user?.preview ? user?.real_role : user?.role]}</p>
          <p className="mt-1 truncate text-sm font-semibold text-white">{user?.preview ? (user?.real_name || user?.name) : user?.name}</p>
          <p className="truncate text-xs text-slate-400">{user?.preview ? (user?.real_email || user?.email) : user?.email}</p>
          {user?.preview && (
            <p className="mt-1.5 rounded bg-amber-400/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-200" data-testid="sidebar-preview-tag">
              Previewing {ROLE_LABEL[user?.preview_role || user?.role]}
            </p>
          )}
          <Button onClick={doLogout} data-testid="logout-button" variant="ghost"
            className="mt-3 h-8 w-full justify-start px-2 text-slate-300 hover:bg-white/10 hover:text-white">
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </Button>
        </div>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-slate-50 md:flex">
      {/* mobile top bar */}
      <div className="flex items-center justify-between bg-[#003057] p-4 text-white md:hidden">
        <div className="flex items-center gap-2 rounded-lg bg-white px-2 py-1">
          <img src={GPA_LOGO} alt="GPA+" className="h-7 w-auto" />
        </div>
        <button onClick={() => setOpen((o) => !o)} data-testid="dash-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && <div className="md:hidden">{Sidebar}</div>}
      <div className="hidden md:block">{Sidebar}</div>

      <main className="flex-1 md:h-screen md:overflow-y-auto">
        <div ref={contentRef} className="mx-auto w-full max-w-7xl px-4 py-6 md:px-8 md:py-8">
          {showEaNotice && (
            <div data-testid="early-access-banner" data-pdf-hide
              className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
              <div className="flex items-start gap-2.5 text-sm text-amber-900">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <span>
                  <strong>Early Access preview.</strong> Calculations, scoring methodologies, career-alignment methods, terminology, reports, and features are still being audited and refined.{" "}
                  <Link to="/app/help#early-access" className="font-semibold underline underline-offset-2 hover:text-amber-950" data-testid="early-access-learn-more">Learn more</Link>
                </span>
              </div>
              <Button size="sm" variant="ghost" onClick={dismissEaNotice} data-testid="dismiss-early-access-btn"
                className="h-8 rounded-full px-3 text-amber-800 hover:bg-amber-100 hover:text-amber-950">
                Dismiss
              </Button>
            </div>
          )}
          {user?.preview && (
            <div data-testid="preview-mode-banner" data-pdf-hide
              className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3">
              <div className="flex items-center gap-2.5 text-sm text-amber-900">
                <Eye className="h-4 w-4 flex-shrink-0" />
                <span>
                  <strong>Previewing {ROLE_LABEL[user.preview_role || user.role]}</strong>
                  {user.preview_persona?.name ? <> · Sample data: <strong>{user.preview_persona.name}</strong></> : null}
                  {" · "}Signed in as <strong>{user.real_name || user.name}</strong> · Read-only
                </span>
              </div>
              <Button size="sm" onClick={doExitPreview} disabled={exiting} data-testid="exit-preview-btn"
                className="rounded-full bg-amber-600 text-white hover:bg-amber-700">
                {exiting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Exit preview
              </Button>
            </div>
          )}
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <h1 className="font-heading text-2xl font-extrabold tracking-tight text-[#003057] sm:text-3xl">{title}</h1>
              {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-2" data-pdf-hide>
              <ReferButton />
              <Button onClick={downloadPdf} disabled={pdfBusy} variant="outline" data-testid="download-pdf-btn"
                className="rounded-full border-[#003057]/20 font-semibold text-[#003057] hover:bg-[#003057]/5">
                {pdfBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
                {pdfBusy ? "Preparing…" : "Download PDF"}
              </Button>
              <Button onClick={doLogout} variant="outline" className="md:hidden" data-testid="logout-button-mobile">
                <LogOut className="mr-2 h-4 w-4" /> Sign out
              </Button>
              <UserMenu />
            </div>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
